import { z } from 'zod'
import { membershipPlanSchema } from '@/features/membership-types/data/schema'

export const queueStatusSchema = z.enum(['waiting', 'active', 'cancelled'])
export const paymentMethodSchema = z.enum([
  'cash',
  'credit_card',
  'debit_card',
  'transfer',
])
export const paymentStatusSchema = z.enum([
  'pending',
  'completed',
  'failed',
  'refund',
])
export const membershipStatusSchema = z.enum([
  'active',
  'expired',
  'refunded',
  'cancelled',
  'scheduled',
])

// ==================== MEMBERSHIP SCHEMA ====================

export const membershipSchema = z.object({
  id: z.string(),
  userId: z.string(),
  type: z.string(),
  planId: z.string().nullable().optional(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
  isActive: z.boolean(),
  status: membershipStatusSchema,
  durationMonths: z.number(),
  // Days added by closure compensations, on top of the plan's duration
  compensatedDays: z.number().optional(),
  priceApplied: z.number(), // en centavos
  notes: z.string().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  plan: membershipPlanSchema.optional().nullable(),
  discount: z
    .object({
      id: z.string(),
      label: z.string(),
      appliedType: z.enum(['percent', 'fixed']),
      appliedValue: z.number(),
      discountAmount: z.number(),
    })
    .nullable()
    .optional(),
  weeklyLimit: z.number().nullable().optional(),
  personalizedTerms: z
    .object({
      id: z.string(),
      membershipId: z.string(),
      sessionsPerWeek: z.number(),
      totalSessions: z.number(),
      createdAt: z.coerce.date(),
      updatedAt: z.coerce.date(),
    })
    .nullable()
    .optional(),
})

export const membershipWithPlanSchema = membershipSchema.extend({
  plan: membershipPlanSchema.nullable(),
})

export const membershipWithUserSchema = membershipSchema.extend({
  user: z.object({
    id: z.string(),
    email: z.email(),
    firstName: z.string(),
    lastName: z.string(),
    rut: z.string().nullable(),
    documentType: z.enum(['rut', 'passport']).default('rut'),
    documentNumber: z.string().optional(),
  }),
})

export const paymentSchema = z.object({
  id: z.string(),
  membershipId: z.string(),
  userId: z.string(),
  amount: z.number(),
  method: paymentMethodSchema,
  status: paymentStatusSchema,
  paymentDate: z.coerce.date(),
  receiptNumber: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  createdAt: z.coerce.date().optional(),
})

export const paymentUserSchema = z.object({
  id: z.string(),
  email: z.string().email(),
  firstName: z.string(),
  lastName: z.string(),
  rut: z.string().nullable(),
  documentType: z.enum(['rut', 'passport']).default('rut'),
  documentNumber: z.string().optional(),
})

export const paymentMembershipSchema = z.object({
  id: z.string(),
  type: z.string(),
  startDate: z.coerce.date(),
  endDate: z.coerce.date(),
})

export const paymentWithRelationsSchema = paymentSchema.extend({
  user: paymentUserSchema,
  membership: paymentMembershipSchema,
})

export const paymentRefundSchema = z.object({
  id: z.string(),
  paymentId: z.string(),
  membershipId: z.string(),
  userId: z.string(),
  amount: z.number(),
  method: paymentMethodSchema,
  refundDate: z.coerce.date(),
  reason: z.string(),
  notes: z.string().nullable().optional(),
  createdAt: z.coerce.date().optional(),
  user: paymentUserSchema,
  membership: paymentMembershipSchema,
})

// ==================== MEMBERSHIP QUEUE SCHEMA ====================

export const membershipQueueSchema = z.object({
  id: z.string(),
  userId: z.string(),
  planId: z.string().nullable().optional(),
  membershipType: z.string(),
  queuePosition: z.number(),
  status: queueStatusSchema,
  startDate: z.coerce.date(),
  createdAt: z.coerce.date(),
})

// ==================== FORM SCHEMAS ====================

export const createMembershipFormSchema = z.object({
  rut: z.string().min(1, 'Usuario es requerido'),
  planId: z.string().min(1, 'Plan is required'),
  durationMonths: z.number().min(1, 'Selecciona una duración'),
  discountId: z.string().optional(),
  startDate: z.date(),
  price: z.number().min(0, 'Price must be positive'), // en PESOS (se convierte en API)
  notes: z.string().optional(),
  // Payment fields
  paymentMethod: paymentMethodSchema,
  paymentDate: z.date().optional(),
  receiptNumber: z.string().optional(),
  paymentNotes: z.string().optional(),
})

export const renewMembershipFormSchema = z.object({
  userEmail: z.string().email('Invalid email'),
  userId: z.string().min(1, 'User is required'),
  planId: z.string().min(1, 'Plan is required'),
  durationMonths: z.number().min(1, 'Selecciona una duración'),
  discountId: z.string().optional(),
  price: z.number().min(0, 'Price must be positive'),
  notes: z.string().optional(),
  // Payment fields
  paymentMethod: paymentMethodSchema,
  paymentDate: z.date().optional(),
  receiptNumber: z.string().optional(),
  paymentNotes: z.string().optional(),
})

export const updateMembershipFormSchema = z
  .object({
    planId: z.string().min(1).optional(),
    startDate: z.date().optional(),
    endDate: z.date().optional(),
    durationMonths: z.number().min(1).optional(),
    price: z.number().min(0).optional(),
    notes: z.string().nullable().optional(),
    weeklyLimit: z.number().optional(),
  })
  .refine(
    (data) => {
      if (data.startDate && data.endDate) {
        return data.endDate > data.startDate
      }
      return true
    },
    {
      message: 'End date must be after start date',
      path: ['endDate'],
    }
  )

// ==================== TYPES ====================

export type MembershipStatus = z.infer<typeof membershipStatusSchema>

// Helper function para calcular estado real de una membresía
export function getMembershipRealStatus(
  membership: Pick<Membership, 'startDate' | 'endDate' | 'isActive' | 'status'>
): MembershipStatus {
  if (membership.status) return membership.status
  const now = new Date()
  if (membership.startDate > now) return 'scheduled'
  if (membership.endDate < now) return 'expired'
  if (membership.isActive) return 'active'
  return 'cancelled'
}

export function canRequestMembershipStartDateEdit(
  membership: Pick<Membership, 'startDate' | 'endDate' | 'isActive' | 'status'>
): boolean {
  const status = getMembershipRealStatus(membership)
  return status === 'scheduled' || status === 'active'
}

export function getMembershipStartDateEditHelpText(
  membership: Pick<Membership, 'startDate' | 'endDate' | 'isActive' | 'status'>
): string {
  const status = getMembershipRealStatus(membership)

  if (status === 'scheduled') {
    return 'Puedes corregir la fecha de inicio de un plan programado.'
  }

  if (status === 'active') {
    return 'Puedes corregir la fecha de inicio si el plan no tiene check-ins registrados. Si ya tiene ingresos, el sistema bloqueara el cambio.'
  }

  return 'La fecha de inicio solo se puede editar en planes programados o activos sin ingresos.'
}

export type Membership = z.infer<typeof membershipSchema>
export type MembershipWithPlan = z.infer<typeof membershipWithPlanSchema>
export type MembershipWithUser = z.infer<typeof membershipWithUserSchema>
export type Payment = z.infer<typeof paymentSchema>
export type PaymentUser = z.infer<typeof paymentUserSchema>
export type PaymentMembership = z.infer<typeof paymentMembershipSchema>
export type PaymentWithRelations = z.infer<typeof paymentWithRelationsSchema>
export type PaymentRefund = z.infer<typeof paymentRefundSchema>
export type MembershipQueue = z.infer<typeof membershipQueueSchema>
export type CreateMembershipForm = z.infer<
  typeof createMembershipFormSchema
> & {
  userId?: string // Internal field used after RUT lookup
}
export type RenewMembershipForm = z.infer<typeof renewMembershipFormSchema>
export type UpdateMembershipForm = z.infer<typeof updateMembershipFormSchema>
